import React, { useState, useEffect } from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
  useAccount,
} from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { loginRequest } from "../config/authConfig";
//import { useAuth } from "../context/AuthContext";
import OpportunityView from "../components/opportunity/OpportunityView";
import TowerView from "../components/towers/TowerView";
import { MESSAGE, PAGINATION } from "../constants/constant";
//import { saveFunctionToBackend, generateSerialNumber, } from "../helpers/FunctionsBackend";
import { useToast } from "../hooks/useToast";
import apiService from "../services/apiService";
//import { formatDate } from "../utils/formatDate";

import ResponsivePagination from "react-responsive-pagination";
import "react-responsive-pagination/themes/classic.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "../assets/styles/DashboardPage.css";

const DashboardPage = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  //const [error, setError] = useState(null);
  const [searchInOpp, setSearchInOpp] = useState("");
  const [searchInTower, setSearchInTower] = useState("");
  const [sortByInOpp, setSortByInOpp] = useState(["data.Date_Bid_Due:desc"]);
  const [noResults] = useState(false);
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  //const { user } = useAuth(); // Add this line to get the user from AuthContext
  const [activeTab, setActiveTab] = useState("opportunities"); // State for Dashboard's 'Active Tab'
  const [dbData, setDbData] = useState(); // State for Database Data
  const [towerData, setTowerData] = useState(); // State for Tower Data
  const [connectionError, setConnectionError] = useState(false);

  const [currentPage, setCurrentPage] = useState(
    PAGINATION.DEFAULT_PAGE_NUMBER
  );
  const [limit, setLimit] = useState(PAGINATION.DEFAULT_PAGE_LIMIT);
  const [totalPages, setTotalpages] = useState(1);
  const [totalCount, setTotalCount] = useState();
  const [sortOrder, setSortOrder] = useState({
    key: "Date_Bid_Due",
    direction: "desc",
  });

  useEffect(() => {
    let isMounted = true;
    
    if (account && !connectionError) {
      const CreateOrUpdateUser = async () => {
        if (accounts.length > 0) {
          const account = accounts[0];
          const success = await refreshAuthToken(account);
          
          // Only proceed with data fetching if authentication was successful
          if (success && isMounted) {
            fetchData();
          }
        }
      };
      CreateOrUpdateUser();
    } else if (!connectionError) {
      // If no account but not in error state, still try to fetch data
      // This will use any existing token in localStorage
      fetchData();
    }
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [
    activeTab,
    currentPage,
    limit,
    searchInOpp,
    searchInTower,
    sortByInOpp,
    account,
    accounts,
    instance,
    connectionError,
  ]);

  const refreshAuthToken = async (account) => {
    if (connectionError) {
      // Don't retry if we're already in an error state
      return false;
    }
    
    try {
      const authCode = await instance.acquireTokenSilent({
        ...loginRequest,
        account: account,
      });
  
      const response = await apiService.get(
        `/auth/loginOrCreateUserWithMicrosoft`,
        {
          params: {
            microsoftId: authCode.account.idTokenClaims.sub,
            email: authCode.account.username,
            name: authCode.account.name,
          },
        }
      );
  
      localStorage.setItem("accessToken", response.data.token.access.token);
      localStorage.setItem("refreshToken", response.data.token.refresh.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      setConnectionError(false);
      return true;
    } catch (error) {
      console.error("Login failed", error);
      setLoading(false);
      setConnectionError(true);
      
      // Only show toast once, not on every retry
      if (!connectionError) {
        addToast("Session expired. Please refresh the page to continue.", "warning");
      }
      return false;
    }
  };

  const changePage = (value) => {
    setCurrentPage(value);
    setLoading(true);
  };

  const handleSearchOpp = (value) => {
    setSearchInOpp(value);
    statusChange();
  };

  const handleSearchTower = (value) => {
    setSearchInTower(value);
    statusChange();
  };

  const changeLimit = (value) => {
    setLimit(value);
    statusChange();
  };

  const handleActiveTab = (value) => {
    setSearchInOpp("");
    setSearchInTower("");
    if (activeTab !== value) {
      setActiveTab(value);
      statusChange();
    }
  };

  const statusChange = () => {
    setCurrentPage(PAGINATION.DEFAULT_PAGE_NUMBER);
    setLoading(true);
  };

  const handleSort = (key) => {
    // const newOrder = sortOrder.direction === 'asc' ? 'desc' : 'asc';
    let direction = "asc";
    if (sortOrder.key === key && sortOrder.direction === "asc") {
      direction = "desc";
    }
    setSortOrder({ key, direction });
    setSortByInOpp([`data.${key}:${direction}`]);
  };

  const fetchData = async () => {
    if (activeTab === "opportunities") {
      await getDataFromDB();
    } else {
      await getTowers();
    }
  };

  // Getting Opportunities from DB

  const getDataFromDB = async (forceRefresh = false) => {
    const queryString = new URLSearchParams({
      page: currentPage,
      limit: limit,
      search: searchInOpp,
      sortBy: sortByInOpp,
      forceRefresh: forceRefresh,
    }).toString();
    try {
      setLoading(true);
      const response = await apiService.get(
        `/opportunity/opportunities?${queryString}`
      );
      if (response.status === 200) {
        let data = response.data.opportunity.results;

        let totalCount = response.data.allOpportunities.length;
        let total_page = Math.ceil(totalCount / limit);
        setDbData(data);
        setTotalCount(totalCount);
        setTotalpages(parseInt(total_page));
        setLoading(false);
      } else {
        addToast(MESSAGE.SERVER_ERROR, "danger");
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const getTowers = async () => {
    const queryString = new URLSearchParams({
      page: currentPage,
      limit: limit,
      search: searchInTower,
    }).toString();
    try {
      let response = await apiService.get(
        `/tower/getTowerListAggregateOpp?${queryString}`
      );
      if (response.status === 200) {
        let towers = response.data.towers.results;
        let totalCount = response.data.allTowers.length;
        let total_page = Math.ceil(totalCount / limit);
        setTotalCount(totalCount);
        setTotalpages(parseInt(total_page));
        setTowerData(towers);
      } else {
        addToast(MESSAGE.SERVER_ERROR, "danger");
      }
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  };

  const handleCreateTower = () => {
    // navigate("/createTower", { state: { towerSerialNumber: searchTerm } });
    navigate("/createTower");
  };

  return (
    <div className="container-fluid p-4">
      <AuthenticatedTemplate>
        <div className="row">
          <div className="col px-0">
            <div className="custom-tabs-container mb-0">
              <ul className="nav nav-tabs custom-tabs" role="tablist">
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${
                      activeTab === "opportunities" ? "active" : ""
                    }`}
                    onClick={() => handleActiveTab("opportunities")}
                  >
                    Opportunities
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${
                      activeTab === "towers" ? "active" : ""
                    }`}
                    onClick={() => handleActiveTab("towers")}
                  >
                    Towers
                  </button>
                </li>
              </ul>
            </div>
            <div className="custom-tab-content">
              <div className="d-flex align-items-center mb-3">
                <div className="flex-grow-1 search_select_option">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={
                      activeTab === "opportunities"
                        ? "Search Opportunities..."
                        : "Search Towers..."
                    }
                    value={
                      activeTab === "opportunities"
                        ? searchInOpp
                        : searchInTower
                    }
                    onChange={(e) => {
                      activeTab === "opportunities"
                        ? handleSearchOpp(e.target.value)
                        : handleSearchTower(e.target.value);
                    }}
                  />
                  <div>
                    <select
                      id={`total_page`}
                      value={limit}
                      onChange={(e) => changeLimit(e.target.value)}
                      required
                      style={{ padding: "6px", marginRight: "4px" }}
                    >
                      {PAGINATION.PAGE_LIST.map((option) => {
                        return (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              </div>

              {activeTab === "opportunities" ? (
                <>
                  {noResults ? (
                    <div className="d-flex flex-column align-items-center">
                      <p>No results found for "{searchInOpp}".</p>
                      <p>Would you like to create {searchInOpp}?</p>
                      <button
                        className="btn btn-primary"
                        onClick={handleCreateTower}
                      >
                        Create Tower
                      </button>
                    </div>
                  ) : (
                    <OpportunityView
                      opportunityData={dbData}
                      totalCount={totalCount}
                      loading={loading}
                      setLoading={setLoading}
                      handleSort={handleSort}
                      sortOrder={sortOrder}
                      getOpportunities={(forceRefresh) =>
                        getDataFromDB(forceRefresh)
                      }
                    />
                  )}
                </>
              ) : (
                <TowerView
                  towerData={towerData}
                  setTowerData={setTowerData}
                  totalCount={totalCount}
                  loading={loading}
                  limit={limit}
                  changeLimit={changeLimit}
                />
              )}
            </div>
            <div
              style={{
                marginTop: "10px",
                display: "flex",
                justifyContent: "right",
              }}
            >
              <ResponsivePagination
                current={currentPage}
                total={totalPages}
                onPageChange={(page) => changePage(page)}
              />
            </div>
          </div>
        </div>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <p>Please log in to view the dashboard.</p>
        <button className="btn btn-primary" onClick={() => navigate("/")}>
          Go to Login
        </button>
      </UnauthenticatedTemplate>
    </div>
  );
};

export default DashboardPage;
